import type { ReactNode } from 'react';
import { ShieldCheck, Sparkles, BadgeCheck, Upload, Layers } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Skeleton, Badge } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useWorkspace, useActivateWorkspace } from '@/api/tiger';
import { OnboardingCss } from './onboarding-css';
import {
  heroWrap,
  heroIcon,
  heroEyebrow,
  heroTitle,
  heroLede,
  stepList,
  stepRow,
  markerAccent,
  stepTitle,
  stepDesc,
} from './onboarding-styles';

/**
 * Single GATE in front of the SSL inventory. One `GET /workspace` decides what
 * renders, so no tenant-scoped child query fires (and floods "Tenant context
 * required" 403s) before the org has a workspace:
 *   - loading        → skeleton
 *   - error (real)   → retry
 *   - workspace null → activation hero (onboarding; no children mounted)
 *   - workspace      → children
 */
export function WorkspaceGate({ children }: { children: ReactNode }) {
  const { data: workspace, isLoading, isError, refetch } = useWorkspace();

  if (isLoading) {
    return (
      <Container size="xl">
        <Header />
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <Skeleton style={{ height: 28, width: 220 }} />
          <Skeleton style={{ height: 160 }} />
        </div>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container size="xl">
        <Header />
        <Alert
          tone="danger"
          title="Couldn't reach the certificate service"
          actions={
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          The managed SSL service is temporarily unavailable. Please try again.
        </Alert>
      </Container>
    );
  }

  if (workspace == null) {
    return (
      <Container size="xl">
        <Header />
        <WorkspaceActivation />
      </Container>
    );
  }

  return <>{children}</>;
}

function Header() {
  return (
    <PageHeader
      icon={<ShieldCheck size={22} />}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          SSL Certificates
          <Badge tone="info" appearance="subtle" size="sm">
            Beta
          </Badge>
        </span>
      }
      description="The managed certificate inventory. Issue free SSL, upload your own, and reuse across every service."
    />
  );
}

const PERKS = [
  {
    icon: <BadgeCheck size={16} />,
    title: 'Free managed SSL',
    desc: "Issue Let's Encrypt certificates with automatic renewal — including wildcards.",
  },
  {
    icon: <Upload size={16} />,
    title: 'Bring your own',
    desc: 'Upload certificates you already have; we keep them safe and track expiry.',
  },
  {
    icon: <Layers size={16} />,
    title: 'One inventory, every service',
    desc: 'Reuse a single certificate across CDN, WAF and more — no re-pasting PEM.',
  },
];

/** First-run onboarding hero — provisions the org's cert workspace on click. */
function WorkspaceActivation() {
  const activate = useActivateWorkspace();

  return (
    <div className="vn-ob-hero" style={heroWrap}>
      <OnboardingCss />

      <span className="vn-ob-halo" style={heroIcon} aria-hidden>
        <ShieldCheck size={30} />
      </span>

      <p style={heroEyebrow}>Managed SSL</p>
      <h2 style={heroTitle}>Enable SSL Certificates for your organization</h2>
      <p style={heroLede}>
        Set up your certificate workspace to start issuing, uploading and reusing SSL across every
        service. It only takes a moment — we&apos;ll set everything up for your org.
      </p>

      <Button
        size="lg"
        loading={activate.isPending}
        startIcon={<Sparkles size={16} />}
        onClick={() => activate.mutate()}
        disabled={activate.isPending}
        style={{ marginTop: 'var(--vn-space-2)' }}
      >
        {activate.isPending ? 'Enabling…' : 'Enable SSL Certificates'}
      </Button>

      {activate.isError && (
        <Alert
          tone="danger"
          title="Couldn't enable SSL Certificates"
          actions={
            <Button size="sm" variant="secondary" onClick={() => activate.reset()}>
              Dismiss
            </Button>
          }
          style={{ marginTop: 'var(--vn-space-2)', textAlign: 'left', maxWidth: 460 }}
        >
          Please try again, or contact your account team if it persists.
        </Alert>
      )}

      <div style={{ ...stepList, marginTop: 'var(--vn-space-5)' }}>
        {PERKS.map((p) => (
          <div key={p.title} style={stepRow}>
            <span style={markerAccent}>{p.icon}</span>
            <div style={{ display: 'grid', gap: 2 }}>
              <p style={stepTitle}>{p.title}</p>
              <p style={stepDesc}>{p.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default WorkspaceGate;
